import { supabase } from '@/lib/supabase';

// 'shared' added in 0023: the designer opened a share sheet. Distinct from
// 'sent', which only a delivery provider can claim.
export type NotificationStatus = 'pending' | 'sent' | 'shared' | 'failed' | 'cancelled';
export type NotificationKind =
  | 'contractor_t_minus_7' | 'contractor_t_minus_2' | 'contractor_t_zero'
  | 'change_order_new' | 'change_order_acknowledged'
  // 0043 — a punch list sent to the vendor it belongs to.
  | 'punch_list'
  // 0048 — a project note sent to a client or a contractor. Notes stopped
  // appearing on the client portal, so this is how one reaches anybody.
  | 'note';

export interface NotificationRow {
  id: string;
  project_id: string;
  contact_id: string | null;
  schedule_entry_id: string | null;
  kind: NotificationKind;
  channel: 'sms' | 'email';
  scheduled_for: string;
  payload: Record<string, unknown>;
  status: NotificationStatus;
  sent_at: string | null;
  /** 0044 — how many times this message has been handed to a share sheet. */
  send_count: number;
  error: string | null;
  created_at: string;
}

export async function listNotificationsForProject(
  projectId: string,
  limit = 50,
): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('project_id', projectId)
    .order('scheduled_for', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as NotificationRow[];
}

export async function cancelNotification(id: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ status: 'cancelled' })
    .eq('id', id)
    .eq('status', 'pending');
  if (error) throw error;
}

/**
 * Reminders waiting to be sent, soonest first.
 *
 * Ascending, unlike the history list: this is a to-do queue, so the most
 * overdue item belongs at the top.
 */
export async function listPendingNotifications(
  projectId: string,
  limit = 50,
): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('project_id', projectId)
    .eq('status', 'pending')
    .order('scheduled_for', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as NotificationRow[];
}

/**
 * Record that the designer handed a reminder to a share sheet.
 *
 * Deliberately 'shared', never 'sent'. navigator.share() resolving means the
 * sheet closed, not that a message left the phone — only a delivery provider
 * can claim 'sent', and nothing here is one.
 *
 * Guarded on status = 'pending' so a double-tap can't overwrite a real
 * provider result if Twilio is ever revived.
 */
export async function markNotificationShared(id: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ status: 'shared', sent_at: new Date().toISOString() })
    .eq('id', id)
    .eq('status', 'pending');
  if (error) throw error;
}

/**
 * Record a message the designer just handed to a share sheet, and count it.
 *
 * Replaces the plain UPDATE for RESENDS: `send_count + 1` can't be expressed
 * through PostgREST without reading the row first and racing itself, so 0044
 * put it in a function. It is SECURITY INVOKER, so the caller's own rules
 * still decide which rows they may touch.
 *
 * Unlike markNotificationShared() this is NOT guarded on status — resending
 * something already shared is the entire point.
 */
export async function recordNotificationSend(id: string): Promise<number> {
  const { data, error } = await supabase.rpc('record_notification_send', { p_id: id });
  if (error) throw error;
  return typeof data === 'number' ? data : 1;
}

/**
 * Record a message this app composed and sent itself — a punch list handed to
 * a vendor (2026-09-22). Written straight to 'shared': it was sent before this
 * row existed, so it must never appear in "Reminders to send".
 *
 * Best-effort at the call site: the message has already left. Losing the
 * record is a smaller loss than an error dialog claiming a send failed when
 * it didn't.
 */
export async function recordSentMessage(input: {
  projectId: string;
  contactId: string | null;
  kind: NotificationKind;
  text: string;
  phone?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('notifications').insert({
    project_id: input.projectId,
    contact_id: input.contactId,
    kind: input.kind,
    channel: 'sms',
    scheduled_for: new Date().toISOString(),
    sent_at: new Date().toISOString(),
    status: 'shared',
    send_count: 1,
    // The whole message, so the history can show and resend exactly what went
    // out rather than re-deriving it from data that may since have changed.
    payload: { text: input.text, to_phone: input.phone ?? null },
  });
  if (error) throw error;
}

/**
 * Retire reminders more than a few days past their send date.
 *
 * Called when the project loads rather than on a schedule — there is no cron
 * while Twilio is parked, and the designer opening the app is a perfectly good
 * trigger. A reminder for work that already started is noise, and a to-do list
 * with noise in it stops being read.
 */
export async function cancelStaleNotifications(
  projectId: string,
  cutoffIso: string,
): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ status: 'cancelled' })
    .eq('project_id', projectId)
    .eq('status', 'pending')
    .lt('scheduled_for', cutoffIso);
  if (error) throw error;
}

export const NOTIFICATION_KIND_LABEL: Record<NotificationKind, string> = {
  punch_list: 'Punch list',
  note: 'Note',
  contractor_t_minus_7: 'Week before',
  contractor_t_minus_2: '2 days before',
  contractor_t_zero: 'Day of',
  change_order_new: 'Change order sent',
  change_order_acknowledged: 'Change order acknowledged',
};
